import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../App";
import { Mail, ArrowLeft, Loader2, CheckCircle, Rocket } from "lucide-react";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setIsSubmitted(true);
      toast.success("Reset instructions sent!");
    } catch (error) {
      // Don't reveal if email exists or not for security
      setIsSubmitted(true);
      toast.success("If an account exists, reset instructions have been sent.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-container flex flex-col items-center justify-center p-4">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="w-16 h-16 rounded-2xl overflow-hidden mx-auto mb-4">
          <img src="/logo192.png" alt="Explorio" className="w-16 h-16" />
        </div>
        <h1 className="text-3xl font-bold font-heading bg-gradient-to-r from-sky-400 to-cyan-300 bg-clip-text text-transparent">
          Explorio
        </h1>
      </div>

      {/* Card */}
      <div className="w-full max-w-md glass-card p-6">
        {!isSubmitted ? (
          <>
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white mb-2">
                Forgot Password?
              </h2>
              <p className="text-zinc-400 text-sm">
                Enter your email and we'll send you instructions to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    className="pl-10 glass-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-password-email"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={isLoading}
                data-testid="forgot-password-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  "Send Reset Instructions"
                )}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              Check Your Email
            </h2>
            <p className="text-zinc-400 text-sm mb-6">
              If an account exists for <span className="text-white">{email}</span>, 
              you'll receive password reset instructions shortly.
            </p>
            <p className="text-zinc-500 text-xs mb-4">
              Didn't receive the email? Check your spam folder or try again.
            </p>
            <Button
              variant="outline"
              className="btn-outline"
              onClick={() => setIsSubmitted(false)}
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-zinc-400 hover:text-white transition-colors flex items-center justify-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
